/**
 * Diagnostics UI aggregates several existing APIs. There is no dedicated
 * `GET /api/diagnostics` in OpenAPI yet (see `functional_reqs_frontend.md` FE-07 —
 * backend may add one later). Mapping:
 * - Backend reachability: `GET /health` (public)
 * - Worker signal (indirect): `GET /api/runs` — heuristic, not a worker ping
 * - Langfuse/datasets: `GET /api/datasets`
 * - Agent validation rows: fields from `GET /api/agents` (cached validation state)
 * - Scheduler-related rows: `GET /api/runs?source=scheduler` (run records, not a scheduler job log)
 */
import { agentsApi } from "./agentsApi";
import { apiRequest } from "./backendApi";

export type HealthState = "pass" | "warn" | "fail";

export type HealthRow = {
  id: string;
  label: string;
  state: HealthState;
  message: string;
  /** True when derived indirectly (not a dedicated health endpoint). */
  indirect?: boolean;
};

export type AgentValidation = {
  agentId: string;
  agentName: string;
  state: HealthState;
  summary: string;
  checkedAt: string;
};

export type SchedulerOutcome = {
  id: string;
  pair: string;
  outcome: "ran" | "skipped" | "failed" | "in_progress";
  reason: string;
  at: string;
};

const toHealthState = (v: "valid" | "warning" | "error" | "not_validated"): HealthState =>
  v === "valid" ? "pass" : v === "warning" || v === "not_validated" ? "warn" : "fail";

type RunsListResponse = {
  data: Array<{
    run_id: string;
    agent_id: string;
    source: "manual" | "scheduler" | "webhook";
    status: "queued" | "running" | "cancelling" | "cancelled" | "completed" | "failed" | "config_error";
    dataset_name?: string | null;
    dataset_id: string;
    error?: string | null;
    created_at: string;
  }>;
  count: number;
  total: number;
};

export const diagnosticsApi = {
  async getSystemHealth(): Promise<HealthRow[]> {
    const rows: HealthRow[] = [];

    try {
      await apiRequest<{ status: string }>("GET", "/health");
      rows.push({
        id: "backend",
        label: "Backend health",
        state: "pass",
        message: "API is reachable and responding.",
      });
    } catch (error) {
      rows.push({
        id: "backend",
        label: "Backend health",
        state: "fail",
        message: error instanceof Error ? error.message : "Backend is unreachable.",
      });
    }

    try {
      const runs = await apiRequest<RunsListResponse>("GET", "/api/runs", {
        query: { page: 1, limit: 10 },
      });
      const hasRunning = runs.data.some((run) => run.status === "running");
      rows.push({
        id: "worker",
        label: "Worker readiness",
        state: hasRunning ? "pass" : "warn",
        indirect: true,
        message: hasRunning
          ? "At least one run is running (indicates queue/worker activity)."
          : "No runs in running state right now (worker may still be idle).",
      });
    } catch (error) {
      rows.push({
        id: "worker",
        label: "Worker readiness",
        state: "fail",
        message: error instanceof Error ? error.message : "Failed to inspect run queue.",
      });
    }

    try {
      await apiRequest<{ data: unknown[] }>("GET", "/api/datasets", {
        query: { page: 1, limit: 1 },
      });
      rows.push({
        id: "langfuse",
        label: "Langfuse connectivity",
        state: "pass",
        message: "Langfuse dataset API is reachable.",
      });
    } catch (error) {
      rows.push({
        id: "langfuse",
        label: "Langfuse connectivity",
        state: "fail",
        message: error instanceof Error ? error.message : "Could not reach Langfuse dataset API.",
      });
    }

    return rows;
  },
  async listAgentOptions(): Promise<Array<{ id: string; name: string }>> {
    const agents = await agentsApi.listAgents();
    return agents.map((a) => ({ id: a.id, name: a.name }));
  },
  async getAgentValidations(selectedAgentId?: string): Promise<AgentValidation[]> {
    const agents = await agentsApi.listAgents();
    const filtered = selectedAgentId ? agents.filter((a) => a.id === selectedAgentId) : agents;
    return filtered.map((a) => ({
      agentId: a.id,
      agentName: a.name,
      state: toHealthState(a.validationState),
      summary: a.validationMessage,
      checkedAt: a.validatedAt,
    }));
  },
  async getSchedulerOutcomes(): Promise<SchedulerOutcome[]> {
    const runs = await apiRequest<RunsListResponse>("GET", "/api/runs", {
      query: { source: "scheduler", page: 1, limit: 20 },
    });
    return runs.data.map((run) => {
      const pairLabel = `${run.agent_id} + ${run.dataset_name || run.dataset_id}`;
      const terminalFailed = run.status === "failed" || run.status === "config_error";
      const inFlight =
        run.status === "queued" || run.status === "running" || run.status === "cancelling";
      const outcome: SchedulerOutcome["outcome"] = terminalFailed
        ? "failed"
        : run.status === "completed"
          ? "ran"
          : run.status === "cancelled"
            ? "skipped"
            : inFlight
              ? "in_progress"
              : "ran";
      const reason =
        run.error ||
        (run.status === "completed"
          ? "Scheduler run completed."
          : run.status === "cancelled"
            ? "Scheduler run cancelled."
            : inFlight
              ? `Run still active (status: ${run.status}).`
              : `Scheduler status: ${run.status}.`);
      return {
        id: run.run_id,
        pair: pairLabel,
        outcome,
        reason,
        at: run.created_at,
      };
    });
  },
};
